<template>
    <div class="whatsapp-chat-starter" :class="{ 'fullscreen-mode': fullscreenMode }">
        <h1 class="gradient-title">{{ $t("message.wpStartSpeech") }}</h1>

        <div class="icon-flow" v-if="fullscreenMode">
            <div class="icon zoho">
                <ZohoIcon />
            </div>
            <div class="arrow"></div>
            <div class="icon supsis">
                <SupsisIcon />
            </div>
            <div class="arrow"></div>
            <div class="icon whatsapp">
                <WhatsappBusinessIcon />
            </div>
        </div>

        <div v-if="loadingZohoRecord" class="loading-overlay">
            <div class="spinner"></div>
        </div>
        <section v-else-if="zohoRecord" class="contact-card">
            <div class="contact-info">
                <div class="info-item">
                    <i class="fas fa-user"></i>
                    <span>{{ zohoRecord.Full_Name }}</span>
                </div>
                <div class="info-item">
                    <i class="fas fa-user-tie"></i>
                    <span>{{ $t("message.owner") }}: {{ zohoRecord.Owner.name }}</span>
                </div>
                <div class="info-item">
                    <i class="fas fa-phone"></i>
                    <span>{{ maskPhoneNumber(zohoRecord.Phone) }}</span>
                </div>
                <div class="info-item">
                    <i class="fas fa-envelope"></i>
                    <span>{{ maskEmail(zohoRecord.Email || "") }}</span>
                </div>
            </div>
        </section>
        <div v-else class="no-record-info">{{ $t("message.noRecordFailed") }}</div>

        <div v-if="!fullscreenMode">
            <label for="channelId">{{ $t("message.channel") }}</label>
            <small>{{$t("message.channelDesc")}}</small>
            <ChannelPicker
                :allowed-types="['whatsapp-qr', 'whatsapp-v2', 'whatsapp-business-api', 'whatsapp-business-api-supsis']"
                :autoSelectFirst="false"
                v-model="channelId"
            />
        </div>
        <div v-else class="fullscreen-content">
            <div class="channel-select-text">{{ $t("message.pleaseSelectStartChat") }}</div>
            <div class="channel-boxes">
                <div
                    v-for="channel in whatsappChannels"
                    :key="channel._id"
                    class="channel-box"
                    :class="{ selected: channelId === channel._id, disabled: !isChannelAvailable(channel) }"
                    @click="selectChannel(channel)"
                >
                    <div class="channel-icon" :class="channel.type">
                        <WhatsappBusinessIcon v-if="channel.type === 'whatsapp-business-api'" />
                        <WhatsappV2 v-else-if="channel.type === 'whatsapp-v2'" />
                        <WhatsappV1 v-else-if="channel.type === 'whatsapp-qr'" />
                        <WhatsappV3Icon v-else-if="channel.type === 'whatsapp-unipile'" />
                    </div>
                    <div class="channel-name">{{ channel.name }}</div>
                    <div class="channel-number">{{ channel.payload.phoneNumber }}</div>
                    <div v-if="!isChannelAvailable(channel)" class="warning-text">{{$t("message.notAuthorizedChannel")}}</div>
                </div>
            </div>
        </div>

        <footer>
            <SupsisButton danger @click="cancel">{{ $t("message.cancel") }}</SupsisButton>
            <SupsisButton primary @click="startChat" :disabled="!zohoRecord">{{ $t("whatsapp_business_api.startChat")
                }}
            </SupsisButton>
        </footer>
        <div v-if="isSending" class="loading-overlay">
            <div class="spinner"></div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import ChannelPicker from "../../../../components/pickers/ChannelPicker.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import ChatWatcher from "../../../../ChatWatcherV2";
import log2 from "../../../../utils/log2";
import WhatsappV1 from "../../Chatbot/pages/integrations/svg/specialWhatsappIcon/WhatsappV1.vue";
import WhatsappV2 from "../../Chatbot/pages/integrations/svg/specialWhatsappIcon/WhatsappV2.vue";
import WhatsappBusinessIcon from "../../Chatbot/pages/integrations/svg/WhatsappBusinessIcon.vue";
import SupsisIcon from "../../Chatbot/pages/integrations/svg/SupsisIcon.vue";
import ZohoIcon from "../../Chatbot/pages/integrations/svg/ZohoIcon.vue";
import { ZOHO_API } from "../../../../api/zoho";
import WhatsappV3Icon from "../../Chatbot/pages/integrations/svg/specialWhatsappIcon/WhatsappV3.vue";

const log = log2("WhatsappChatStarterWithZoho");
export default Vue.extend({
    name: "WhatsappChatStarterWithZoho",
    components: {
        ZohoIcon,
        SupsisIcon,
        SupsisButton,
        ChannelPicker,
        WhatsappV1,
        WhatsappV2,
        WhatsappBusinessIcon,
        WhatsappV3Icon,
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            siteId: (state) => state.site.site._id,
            user: (state) => state.auth.user,
            whatsappChannels: (state) => state.channel.channels.filter((x) => x.type.startsWith("whatsapp")),
        }),

        ...mapGetters({}),
    },
    props: {
        defaultPhoneNumber: {
            type: String,
        },
        defaultChannelId: {
            type: String,
        },
        phoneNumberOverride: {
            type: Boolean,
            default: true,
        },
        channelOverride: {
            type: Boolean,
            default: true,
        },
        defaultName: {
            type: String,
        },
        defaultEmail: {
            type: String,
        },
    },
    data() {
        log("data");
        return {
            fullscreenMode: true,
            phoneNumber: this.$route.query.phone || this.defaultPhoneNumber,
            name: this.$route.query.name || this.defaultName,
            email: this.$route.query.email || this.defaultEmail,
            zohoContactId: this.$route.query.id,
            module: this.$route.query.module || "Leads",
            channelId: this.defaultChannelId,
            message: "",
            isSending: false,
            zohoRecord: null,
            loadingZohoRecord: false,
        };
    },
    methods: {
        getZohoRecord() {
            this.loadingZohoRecord = true;
            ZOHO_API.getRecordById(this.module, this.zohoContactId)
                .then((data) => {
                    let number = [data.Phone, data.Telefon, data.Mobile, data.GSM, data.Phone1].find((x) => {
                        const number = (x || "").toString();
                        return !number.includes("*") && number.length > 6;
                    });
                    number = number || "";
                    number = number.replace(/[^0-9]/g, "");
                    this.zohoRecord = data;
                    this.phoneNumber = number;
                    this.name = this.zohoRecord.Full_Name;
                    this.email = this.zohoRecord.Email;
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loadingZohoRecord = false;
                });
        },
        checkValidation() {
            log("checkValidation", {
                phoneNumber: this.phoneNumber,
                channelId: this.channelId,
            });
            //phone number validation rules = only numeric and length 6
            if (!this.phoneNumber || this.phoneNumber.length < 6 || !/^\d+$/.test(this.phoneNumber)) {
                this.$message.error(this.$t("auto.invalidPhoneNumber"));
                return false;
            }
            if (!this.channelId) {
                this.$message.error(this.$t("message.selectChannel"));
                return false;
            }
            return true;
        },
        async startChat() {
            log("startChat", {
                phoneNumber: this.phoneNumber,
                channelId: this.channelId,
                validate: this.checkValidation(),
            });
            if (!this.checkValidation()) return;
            const channel = this.whatsappChannels.find((x) => x._id === this.channelId);
            this.isSending = true;

            try {
                const leadId = this.module === "Leads" ? this.zohoContactId : null;
                const leadInfo = this.module === "Leads" ? this.zohoRecord : null;
                const contactId = this.module === "Contacts" ? this.zohoContactId : null;
                const contactInfo = this.module === "Contacts" ? this.zohoRecord : null;
                await Promise.race([
                    ChatWatcher.startChatImmediately({
                        whatsappNumber: this.phoneNumber,
                        channelId: this.channelId,
                        channelType: channel.type,
                        name: this.name,
                        email: this.email,
                        zohoInfo: {
                            leadId,
                            leadInfo,
                            contactId,
                            contactInfo,
                        },
                    }),
                    new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 10000)),
                ]);

                if (!this.fullscreenMode) this.$emit("close");
            } catch (error) {
                this.$message.error(this.$t("message.chatStartError"));
            } finally {
                this.isSending = false;
            }
        },
        isChannelAvailable(channel) {
            const authUser = this.$store.state.auth.user;
            const isSuperAdmin = authUser.isSuperAdmin || authUser.role?.isRoot || authUser.isOwner;

            if (isSuperAdmin) return true;

            const visibility = channel.visibility || { users: [], departments: [] };
            visibility.users = Array.isArray(visibility.users) ? visibility.users : [];
            visibility.departments = Array.isArray(visibility.departments) ? visibility.departments : [];

            const userDepartmentIds = Array.isArray(authUser.subjects)
                ? authUser.subjects.map((x) => (typeof x === "object" ? x._id.toString() : x.toString()))
                : [];

            const isPublic = visibility.users.length === 0 && visibility.departments.length === 0;
            if (isPublic) return true;

            const isVisibleToUser = visibility.users.some((x) => x.toString() === authUser._id.toString());
            const isVisibleToDepartment = visibility.departments.some(
                (x) => userDepartmentIds.includes(x.toString()) || userDepartmentIds.length === 0
            );

            return isVisibleToUser || isVisibleToDepartment;
        },
        selectChannel(channel) {
            if (this.isChannelAvailable(channel)) {
                this.channelId = channel._id;
            }
        },
        cancel() {
            this.$router.push("/");
        },
        maskPhoneNumber(phoneNumber) {
            if (!phoneNumber) return "";
            return phoneNumber.replace(/\d(?=\d{4})/g, "*");
        },
        maskEmail(email) {
            if (!email) return "";
            const [username, domain] = email.split("@");
            return `${username.slice(0, 2)}***@${domain}`;
        },
    },
    watch: {
        whatsappChannels(newChannels) {
            // Eğer henüz bir kanal seçilmediyse ve en az bir erişilebilir kanal varsa, ilk erişilebilir kanalı otomatik olarak seç
            if (!this.channelId) {
                const firstAvailableChannel = newChannels.find((channel) => this.isChannelAvailable(channel));
                if (firstAvailableChannel) {
                    this.channelId = firstAvailableChannel._id;

                    // Eğer sadece 1 erişilebilir kanal varsa ve numara mevcutsa, sohbeti otomatik olarak başlat
                    const availableChannels = newChannels.filter((channel) => this.isChannelAvailable(channel));
                    if (availableChannels.length === 1 && this.phoneNumber) {
                        this.startChat();
                    }
                }
            }
        },
    },
    created() {
        // Eğer varsayılan bir kanal idsi sağlanmışsa ve bu kanala erişim varsa, onu kullan
        if (this.defaultChannelId) {
            const defaultChannel = this.whatsappChannels.find((channel) => channel._id === this.defaultChannelId);
            if (defaultChannel && this.isChannelAvailable(defaultChannel)) {
                this.channelId = this.defaultChannelId;
            }
        }
        this.getZohoRecord();
    },
});
</script>

<style scoped lang="scss">
.whatsapp-chat-starter {
    display: flex;
    flex-direction: column;
    padding: 20px;

    .gradient-title {
        font-size: 24px;
        font-weight: bold;
        margin-bottom: 20px;
        text-align: center;
        background: linear-gradient(45deg, #25d366, #128c7e);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    h1 {
        font-size: 20px;
        margin-bottom: 20px;
    }

    div {
        margin-bottom: 20px;

        label {
            font-size: 14px;
            margin-bottom: 5px;
        }

        small {
            font-size: 12px;
            color: #999;
            margin-bottom: 5px;
        }

        input {
            width: 100%;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 5px;
        }
    }

    footer {
        margin-top: 20px;
        display: flex;
        gap: 10px;
        justify-content: space-between;
    }

    &.fullscreen-mode {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 999;
        background-color: white;
        width: 100vw;
        height: 100vh;

        // İçerikleri ortada göstermek için
        justify-content: center;
        align-items: center;

        > div {
            max-width: 70%;
            width: 100%;
        }

        .fullscreen-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            flex: 1;
            min-height: 0;
            overflow: hidden;
        }
    }

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        width: 100vw !important;
        max-width: 100vw !important;

        .spinner {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            border: 6px solid #ccc;
            border-top-color: #333;
            animation: spin 1s ease-in-out infinite;
        }
    }

    .contact-info {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 20px;

        span {
            margin-bottom: 5px;
        }
    }

    .no-record-info {
        text-align: center;
        color: red;
        margin-bottom: 20px;
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.channel-boxes {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
    margin-bottom: 20px;
    max-height: 50vh;
    overflow-y: auto;
    padding: 10px;

    .channel-box {
        width: 200px;
        padding: 20px;
        border: 1px solid #ccc;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.3s;
        text-align: center;

        &:hover {
            background-color: #f0f0f0;
        }

        &.selected {
            background-color: #e0e0e0;
            font-weight: bold;
        }

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .channel-icon {
            width: 70px;
            height: 70px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            margin: 0 auto 15px;
            overflow: hidden;

            &.whatsapp-business-api {
                background: #58ca64;
            }

            &.whatsapp-unipile {
                background: #25d366;
            }
            &.whatsapp-v2 {
                background: #58ca64;
            }

            &.whatsapp-qr {
                background: #58ca64;
            }

            svg {
                width: 60%;
            }
        }

        .channel-name {
            font-weight: bold;
            margin-bottom: 5px;
        }

        .channel-number {
            font-size: 14px;
            color: #666;
        }

        .warning-text {
            color: red;
            font-size: 12px;
            margin-top: 5px;
        }
    }

    .channel-select-text {
        margin-bottom: 30px;
        font-size: 18px;
        font-weight: bold;
    }
}

.icon-flow {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;

    .icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 10px;
        padding: 5px;

        img {
            max-width: 60%;
            max-height: 60%;
        }

        &.zoho {
            background-color: #f0f0f0;
        }

        &.supsis {
            background-color: #f0f0f0;
        }

        &.whatsapp {
            background-color: #25d366;
        }
    }

    .arrow {
        width: 30px;
        height: 2px;
        background-color: #ccc;
        position: relative;
        margin: 0 10px;

        &::before {
            content: "";
            position: absolute;
            right: -5px;
            top: -5px;
            width: 0;
            height: 0;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-left: 6px solid #ccc;
        }

        &::after {
            content: "";
            position: absolute;
            right: -5px;
            bottom: -5px;
            width: 0;
            height: 0;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-left: 6px solid #ccc;
        }
    }
}

.contact-card {
    background-color: #f0f0f0;
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;

    .attach-icon {
        margin-right: 10px;
        color: #888;
    }

    .contact-info {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        flex: 1;

        .info-item {
            display: flex;
            align-items: center;
            margin-bottom: 10px;

            i {
                margin-right: 5px;
                color: #333;
            }

            span {
                font-size: 16px;
            }
        }
    }
}
</style>
